/**
 * useOmnibarFocusShortcut
 *
 * Registers global keyboard shortcuts that route into the omnibar,
 * hijacking the browser's defaults so the bar is the canonical entry
 * point for both searching and capturing:
 *
 *   - `⌘+F` / `Ctrl+F` → flip to **Ask** mode (the "search" half of the
 *     bar). Replaces the browser's native Find-in-page. Also selects
 *     the input's current value when non-empty, matching the
 *     browser-Find / address-bar convention so the user can
 *     immediately overtype the existing query instead of appending.
 *   - `⌘+D` / `Ctrl+D` *or* `⌘+E` / `Ctrl+E` → cycle into **Add** mode
 *     and its expanded "Focus mode" panel. Both bindings drive the
 *     exact same handler so either muscle memory works; the catalog
 *     keeps two separate spec entries (`omnibarFocusAdd` for ⌘D and
 *     `omnibarFocusAddAlt` for ⌘E) so a future cheatsheet UI lists
 *     both. Each is otherwise preempted by a browser default — ⌘D is
 *     "bookmark this page", ⌘E is Chrome's address-bar-search — but
 *     the shortcut manager registers in capture phase and
 *     `preventDefault`s the event before the browser's native handler
 *     sees it. Preserves any in-flight draft text and caret position
 *     — Add mode is for extending a capture, not searching, so a
 *     select-all would be destructive here.
 *
 * The shortcuts are TRULY GLOBAL: mounted at the `LoggedInShell` level
 * so they survive any route subtree unmount, and the handler runs
 * unconditionally (no `enabled` gate) so the browser's bookmark / find
 * default never wins on a logged-in route — even on screens that don't
 * host the omnibar (e.g. /dev/*, /components, /exp/postit-lab). On
 * those routes the bridge's input handle is unregistered; the handler
 * detects that and falls back to navigating home (which mounts the
 * bar) plus queuing focus on the next register, so the bar lights up
 * the moment it appears.
 *
 * The Add cycle has three states the user moves through, in order:
 *
 *   1. **Anywhere else** → engage Add mode. Switches mode to `add`,
 *      collapses the bar back to its compact pill if it was expanded
 *      from a previous interaction, and focuses the input. This is
 *      always step one regardless of whether the bar was idle, in Ask
 *      mode, or focused-but-blurred.
 *   2. **Add mode + input already focused + collapsed** → expand into
 *      Focus mode. Drives the same `setExpanded(true)` path the
 *      expand toggle button uses; the bar's existing FLIP morph runs.
 *   3. **Add mode + input focused + expanded (Focus mode)** → exit
 *      Focus mode. Collapses back to the compact pill while keeping
 *      the input focused, so the user lands in step 1's resting state
 *      (collapsed + focused) and a fourth press re-opens Focus mode.
 *
 * The "+ input focused" gate matters for state 2: if the bar is in
 * Add mode but the user clicked elsewhere and blurred it, the next
 * Add-mode press should refocus the input (state 1) rather than
 * expanding — expanding a blurred bar would feel like a non-sequitur.
 * The focus check is read off the bridge's input handle so the hook
 * doesn't reach into `document.activeElement` directly.
 *
 * Focus path branches on whether the mode is actually changing:
 *   - **No mode change** (the user is already in the target mode):
 *     focus the currently-registered handle directly. Works even
 *     when focus is already inside the bar — `selectAll()` lets a
 *     repeated press overtype the existing draft, address-bar style
 *     (Ask mode only — Add preserves the in-flight draft).
 *   - **Mode change**: the textarea ↔ editor element swap unmounts
 *     the currently-registered input. A synchronous focus call here
 *     would land on the about-to-die element and leak focus to
 *     `body`. Instead, queue the focus via the bridge's
 *     `focusOnNextRegister` flag — `<DraftBar>`'s mode-keyed effect
 *     re-registers the new handle as part of the same React commit,
 *     and the bridge consumes the pending intent in that very call,
 *     so focus moves directly old → new without ever bouncing off
 *     `body`.
 *
 * Mount once at `LoggedInShell` — registering at AppScreen level meant
 * any AppScreen unmount / remount (e.g. transient route swaps, the dev
 * / components / experimental screens that don't render AppScreen at
 * all) silently dropped the binding and let the browser default fire.
 * LoggedInShell is the highest stable mount point across every
 * authenticated route, so the binding is active for the full session.
 */
import { useNavigate } from "react-router-dom";
import { useApp } from "@/state";
import { SHORTCUTS } from "@/keyboard/shortcuts";
import { useShortcut } from "@/keyboard/use-shortcut";
import { focusAndSelectOmnibarInput, focusAndSelectOmnibarInputOnNextRegister, focusOmnibarInput, focusOmnibarInputOnNextRegister, getOmnibarExpand, getOmnibarInput, } from "./omnibar-bridge";
export function useOmnibarFocusShortcut() {
    const app = useApp();
    const navigate = useNavigate();
    // Both ⌘D and ⌘E drive the same Add-mode cycle. Defining the handler
    // once and registering it against both specs guarantees identical
    // behaviour across the two bindings (cycle state lives in app state
    // + bridge handles, not in the keystroke). `useShortcut` captures
    // the handler via a ref on every render so closing over the latest
    // `app` / bridge values isn't an issue.
    const handleAddShortcut = () => {
        const input = getOmnibarInput();
        // Bar isn't mounted (dev / components / experimental route, or a
        // route transition that briefly tore the bar down). Stage the
        // intent on the bridge's "next register" slot and navigate home
        // — when `<DraftBar>` mounts at the destination it re-registers
        // its input handle and the bridge consumes the pending focus
        // intent in the same call, so the bar lights up the instant the
        // user lands. `setOmnibarMode("add")` is mirrored so the bar
        // wakes up in the right mode even if its persisted state was
        // "ask" from a previous session.
        if (input === undefined) {
            app.setOmnibarMode("add");
            focusOmnibarInputOnNextRegister();
            navigate("/");
            return;
        }
        const isChangingMode = app.state.ui.omnibarMode !== "add";
        const expand = getOmnibarExpand();
        const wasInputFocused = input.isFocused() === true;
        const wasExpanded = expand?.isExpanded() === true;
        // Always commit to Add mode first so downstream branches can
        // assume the bar is in the right mode regardless of where the
        // user came from. `setOmnibarMode` no-ops when already on `add`.
        app.setOmnibarMode("add");
        // Cycle step 3: collapse out of Focus mode. Only fires when
        // we're already engaged with Add mode (focused + expanded) — a
        // press from anywhere else falls through to the engage branch
        // below, which forces a clean compact landing state instead.
        if (isChangingMode === false && wasInputFocused && wasExpanded) {
            expand?.setExpanded(false);
            // Keep focus on the input; no re-focus call needed since
            // `setExpanded(false)` doesn't blur and the user is already
            // there.
            return;
        }
        // Cycle step 2: expand into Focus mode. Same gate as step 3 —
        // already in Add mode AND focused — but the bar is currently
        // collapsed, so flipping `setExpanded(true)` runs the FLIP morph
        // and lands the user in the centred panel.
        if (isChangingMode === false && wasInputFocused && wasExpanded === false) {
            expand?.setExpanded(true);
            return;
        }
        // Cycle step 1: engage. Either we just switched into Add mode,
        // or we were in Add mode but the input was blurred. Either way,
        // land in the canonical "compact + focused" resting state so
        // the next Add-mode press deterministically opens Focus mode.
        // Force-collapse handles the rare case of being in Ask mode
        // while the bar happened to be expanded — the user asked to
        // "enter Add mode", which means the standard pill, not someone
        // else's expanded panel inherited across the mode swap.
        if (wasExpanded) {
            expand?.setExpanded(false);
        }
        if (isChangingMode) {
            focusOmnibarInputOnNextRegister();
        }
        else {
            focusOmnibarInput();
        }
    };
    const handleAskShortcut = () => {
        const input = getOmnibarInput();
        // Same "navigate home then focus on next register" fallback as
        // the Add handler — see comment above. Ask mode uses the
        // focus-AND-select variant so the user's existing query is
        // selected for immediate overtype on landing.
        if (input === undefined) {
            app.setOmnibarMode("ask");
            focusAndSelectOmnibarInputOnNextRegister();
            navigate("/");
            return;
        }
        const isChangingMode = app.state.ui.omnibarMode !== "ask";
        app.setOmnibarMode("ask");
        if (isChangingMode) {
            focusAndSelectOmnibarInputOnNextRegister();
        }
        else {
            focusAndSelectOmnibarInput();
        }
    };
    useShortcut(SHORTCUTS.omnibarFocusAdd, handleAddShortcut);
    useShortcut(SHORTCUTS.omnibarFocusAddAlt, handleAddShortcut);
    useShortcut(SHORTCUTS.omnibarFocusAsk, handleAskShortcut);
}
